/**
 * App-store screenshot scenes. Each slug gets its own page at `/mobile/jigsaw/screenshot/:slug`.
 *
 * Google Play (`JIGSAW_SCREENSHOT_SCENES`) keeps the marketing layouts.
 * Apple iPhone 6.7" / iPad 13" (`JIGSAW_APPLE_SCREENSHOT_SCENES`) uses in-app UI only, always the
 * LIGHT theme of the Android Jigsaw Muse app (see `styles/androidJigsawTokens.js`).
 * Pass `?device=iphone67` or `?device=ipadPro13` to load the Apple set.
 *
 * `storyId` — same UUID as `/puzzle/play-jigsaw/:storyId`.
 * `expectedGrid` — picks the N×N template on that story.
 * `marketing` — store look: hero | sorting | journey | giant | daily | vault | colorBasket | tools | victory.
 * `piecesLabel` / `difficultyLabel` — marketing pills (may differ from actual grid²).
 * `promoHeadline` / `promoSubtitle` — Title Case headline + sentence subtitle above the board.
 */

const APPLE_SCREENSHOT_DEVICES = new Set(["iphone67", "ipadPro13"]);

/** Store-screenshot puzzles from admin jigsaw browse (all publish 6/9/12/16/24/32). */
export const JIGSAW_SCREENSHOT_STORY_IDS = [
    "22f1d454-e4e8-42c7-a237-657c7af456a2",
    "3b4bc555-edb3-4a4b-b83c-1b1fba672c32",
    "c887b87b-980b-46c9-9fc5-7cca8a2bfdd2",
    "e24a914a-9984-4ab9-9d2f-ab84beb1d53b",
    "78180eee-8107-4334-8e30-3274f776107a",
    "bae2fc88-a2c3-4367-954b-b60b8ac71b74",
    "1b24b843-f3df-4539-81cf-2755f4a01450",
    "61a53733-4303-453d-bf3f-a9816ab48b09",
    "87996ede-009e-4dbc-acc6-18007eb51ef1",
    "6a305f1f-20fe-40dc-91c8-c960d706a70d",
];

const screenshotStory = (index) => JIGSAW_SCREENSHOT_STORY_IDS[index];
const screenshotGalleryPrepend = (indexes) =>
    indexes.map((index) => ({ storyId: screenshotStory(index) }));

export function isAppleScreenshotDevice(device) {
    return APPLE_SCREENSHOT_DEVICES.has(String(device || "").trim());
}

const SCENE_DEFAULTS = {
    jigsawId: null,
    emptyRatioMin: 0.08,
    emptyRatioMax: 0.12,
    emptyZone: "middle",
    layout: "singleBoard",
    marketing: null,
    hidePills: true,
    hideStoryMeta: true,
    piecesLabel: null,
    difficultyLabel: null,
    galleryPrepend: null,
    galleryTopCount: 0,
};

export const JIGSAW_SCREENSHOT_SCENES = [
    {
        ...SCENE_DEFAULTS,
        slug: "01",
        label: "Hero",
        storyId: screenshotStory(0),
        expectedGrid: 6,
        marketing: "hero",
        emptyRatioMin: 0,
        emptyRatioMax: 0,
        emptyRatio: 0,
        piecesLabel: "36",
        difficultyLabel: "Easy",
        promoHeadline: "Savor Every Piece",
        promoSubtitle: "Premium fantasy jigsaws. Crisp interlocking play. Easy example — other puzzles go up to 1,024 pieces.",
    },
    {
        ...SCENE_DEFAULTS,
        slug: "02",
        label: "Color Baskets",
        // Easy 6×6 board; demo uses a few large color trays (API baskets aren't available on 6×6).
        storyId: screenshotStory(1),
        expectedGrid: 6,
        marketing: "colorBasket",
        layout: "colorBasketDemo",
        promoHeadline: "Color Baskets",
        promoSubtitle:
            "A faster way to complete a difficult puzzle!\nPick a color tray, assemble it, then deploy to the board.",
    },
    {
        ...SCENE_DEFAULTS,
        slug: "03",
        label: "Sorting",
        storyId: screenshotStory(2),
        expectedGrid: 9,
        marketing: "sorting",
        emptyRatioMin: 0.35,
        emptyRatioMax: 0.45,
        piecesLabel: "45",
        promoHeadline: "Smooth Sorting Trays",
        promoSubtitle: null,
    },
    {
        ...SCENE_DEFAULTS,
        slug: "04",
        label: "Immersive",
        storyId: screenshotStory(5),
        expectedGrid: 32,
        marketing: "giant",
        emptyRatioMin: 0,
        emptyRatioMax: 0,
        emptyRatio: 0,
        piecesLabel: "1024",
        difficultyLabel: null,
        promoHeadline: "See the Whole Masterpiece",
        promoSubtitle: null,
    },
    {
        ...SCENE_DEFAULTS,
        slug: "05",
        label: "Giant",
        storyId: screenshotStory(3),
        expectedGrid: 32,
        marketing: "giant",
        emptyRatioMin: 0,
        emptyRatioMax: 0,
        emptyRatio: 0,
        piecesLabel: "1024",
        difficultyLabel: null,
        promoHeadline: "Master the Giant 1024 Grid!",
        promoSubtitle: null,
    },
    {
        ...SCENE_DEFAULTS,
        slug: "06",
        label: "Daily",
        storyId: screenshotStory(4),
        expectedGrid: 12,
        marketing: "daily",
        emptyRatioMin: 0,
        emptyRatioMax: 0,
        emptyRatio: 0,
        promoHeadline: "Daily Masterpiece Challenge",
        promoSubtitle: "Fresh art every day. Climb the board.",
    },
    {
        ...SCENE_DEFAULTS,
        slug: "07",
        label: "Vault",
        layout: "vaultMenu",
        marketing: "vault",
        galleryTopCount: 0,
        galleryPrepend: screenshotGalleryPrepend([0, 1, 2, 3, 4, 5]),
        storyId: screenshotStory(0),
        promoHeadline: "Unlock 1000+ Masterpieces",
        promoSubtitle: null,
    },
    {
        ...SCENE_DEFAULTS,
        slug: "08",
        label: "Gallery",
        layout: "twoColumnGrid",
        marketing: "gallery",
        galleryTopCount: 0,
        galleryPrepend: screenshotGalleryPrepend([0, 1, 2, 3, 4, 5, 6, 7, 8, 9]),
        storyId: screenshotStory(1),
        promoHeadline: "Your Masterpiece Vault",
        promoSubtitle: "Every piece locked. Pure celebration.",
    },
];

const APPLE_SCENE_DEFAULTS = {
    jigsawId: null,
    emptyRatio: 0,
    emptyRatioMin: 0,
    emptyRatioMax: 0,
    emptyZone: "middle",
    layout: "singleBoard",
    marketing: null,
    theme: "light",
    hidePills: true,
    hideStoryMeta: false,
};

const APPLE_PUZZLE = {
    meadow: screenshotStory(5),
    oak: screenshotStory(1),
    tabby: screenshotStory(8),
    classroom: screenshotStory(9),
    vienna: screenshotStory(2),
    mexico: screenshotStory(3),
    fashion: screenshotStory(6),
    extraC: screenshotStory(4),
};

function applePlayScene(slug, label, storyId, { emptyRatio = 0.22, showFullImage = false } = {}) {
    return {
        ...APPLE_SCENE_DEFAULTS,
        slug,
        label,
        layout: "singleBoard",
        theme: "light",
        storyId,
        expectedGrid: 6,
        emptyRatio,
        emptyZone: "random",
        showFullImage,
    };
}

/** App Store iPhone 6.7" / iPad 13" — home, gallery, packs, and play boards. */
export const JIGSAW_APPLE_SCREENSHOT_SCENES = [
    { ...APPLE_SCENE_DEFAULTS, slug: "01", label: "Country", layout: "homeDashboard", storyId: APPLE_PUZZLE.meadow, publishedLabel: "33 minutes ago" },
    {
        ...APPLE_SCENE_DEFAULTS,
        slug: "02",
        label: "Gallery",
        layout: "archiveMenu",
        theme: "light",
        storyId: APPLE_PUZZLE.classroom,
        galleryPrepend: [
            { storyId: APPLE_PUZZLE.meadow },
            { storyId: APPLE_PUZZLE.oak },
            { storyId: APPLE_PUZZLE.tabby },
            { storyId: APPLE_PUZZLE.classroom },
            { storyId: APPLE_PUZZLE.vienna },
            { storyId: APPLE_PUZZLE.mexico },
            { storyId: APPLE_PUZZLE.fashion },
            { storyId: APPLE_PUZZLE.extraC },
        ],
    },
    {
        ...APPLE_SCENE_DEFAULTS,
        slug: "03",
        label: "Premium packs",
        layout: "premiumPacks",
        storyId: APPLE_PUZZLE.tabby,
        galleryPrepend: [
            { storyId: APPLE_PUZZLE.meadow },
            { storyId: APPLE_PUZZLE.oak },
            { storyId: APPLE_PUZZLE.tabby },
            { storyId: APPLE_PUZZLE.classroom },
            { storyId: APPLE_PUZZLE.vienna },
            { storyId: APPLE_PUZZLE.mexico },
            { storyId: APPLE_PUZZLE.fashion },
            { storyId: APPLE_PUZZLE.extraC },
        ],
    },
    applePlayScene("04", "Fantasy", APPLE_PUZZLE.oak),
    applePlayScene("05", "City", APPLE_PUZZLE.vienna),
    applePlayScene("06", "Meadow", APPLE_PUZZLE.mexico),
    {
        ...APPLE_SCENE_DEFAULTS,
        slug: "07",
        label: "Choose level",
        layout: "chooseLevel",
        theme: "light",
        storyId: APPLE_PUZZLE.fashion,
        galleryPrepend: [
            { storyId: APPLE_PUZZLE.meadow },
            { storyId: APPLE_PUZZLE.oak },
            { storyId: APPLE_PUZZLE.tabby },
            { storyId: APPLE_PUZZLE.classroom },
            { storyId: APPLE_PUZZLE.vienna },
            { storyId: APPLE_PUZZLE.mexico },
            { storyId: APPLE_PUZZLE.fashion },
            { storyId: APPLE_PUZZLE.extraC },
        ],
    },
    {
        ...APPLE_SCENE_DEFAULTS,
        slug: "08",
        label: "Daily",
        layout: "dailyMenu",
        storyId: APPLE_PUZZLE.extraC,
    },
];

export function getJigsawScreenshotScenes(device) {
    return isAppleScreenshotDevice(device)
        ? JIGSAW_APPLE_SCREENSHOT_SCENES
        : JIGSAW_SCREENSHOT_SCENES;
}

/** @param {string} slug */
export function getScreenshotScene(slug, device) {
    return getJigsawScreenshotScenes(device).find((s) => s.slug === slug) ?? null;
}

/** True when the scene has a story id and can load a board/gallery. */
export function isScreenshotSceneConfigured(scene) {
    if (scene?.layout === "journeyMap") return true;
    if (scene?.layout === "archiveMenu") return true;
    if (scene?.layout === "chooseLevel") return true;
    if (scene?.layout === "homeDashboard") return true;
    if (scene?.layout === "dailyMenu") return true;
    if (scene?.layout === "premiumPacks") return true;
    if (scene?.layout === "colorBasketDemo" || scene?.marketing === "colorBasket") return true;
    return Boolean(scene?.storyId && String(scene.storyId).trim());
}

export function isAppleArchiveMenuScene(scene) {
    return scene?.layout === "archiveMenu";
}

export function isAppleChooseLevelScene(scene) {
    return scene?.layout === "chooseLevel";
}

export function isAppleHomeScene(scene) {
    return scene?.layout === "homeDashboard";
}

/** Home → Daily menu (date nav + puzzle card + PLAY). */
export function isAppleDailyScene(scene) {
    return scene?.layout === "dailyMenu";
}

export function isApplePremiumPacksScene(scene) {
    return scene?.layout === "premiumPacks";
}

/** Color Baskets marketing half-grid (landing UI; may load a live story). */
export function isColorBasketDemoScene(scene) {
    return scene?.layout === "colorBasketDemo" || scene?.marketing === "colorBasket";
}

/** Scenes that show a two-column image gallery instead of one full puzzle board. */
export function isTwoColumnGalleryScene(scene) {
    return scene?.layout === "twoColumnGrid";
}

/** Immersive vault pack-folder menu (loads gallery images for pack covers). */
export function isVaultMenuScene(scene) {
    return scene?.layout === "vaultMenu";
}

/** Pure marketing journey map (no jigsaw API). */
export function isJourneyMapScene(scene) {
    return scene?.layout === "journeyMap" || scene?.marketing === "journey";
}
